/**
 * Organizational Simulation (Normal Core Phase 7 Tier-2, MASTER-PRD §6.5.7).
 * The Simulate surface runs counterfactual scenarios over the event log —
 * pure local compute server-side; a run enters the audit chain as
 * `simulation:completed`.
 */

import { z } from "zod";
import type { Operation } from "../types";

const outcomeSummary = z.object({
  total: z.number(),
  fulfilled: z.number(),
  fulfillmentRate: z.number(),
  byKind: z.record(
    z.string(),
    z.object({ total: z.number(), fulfilled: z.number() }),
  ),
});

const scenarioResult = z.object({
  scenario: z.string(),
  baseline: outcomeSummary,
  projected: outcomeSummary,
  fulfilledDelta: z.number(),
  explanation: z.string(),
});

export type SimulateOutcomeSummary = z.infer<typeof outcomeSummary>;
export type SimulateScenarioResult = z.infer<typeof scenarioResult>;

const optionsResponse = z.object({
  /** Commitment kinds present in the stream (the remove-kind/inflow scopes). */
  kinds: z.array(z.string()),
  /** Actors with declared capacity (the hire scenario's current staff). */
  staffed: z.number(),
  totalCommitments: z.number(),
  openCommitments: z.number(),
});

export type SimulateOptionsResponse = z.infer<typeof optionsResponse>;

export const SIMULATE_SCENARIOS = ["inflow", "hire", "remove-kind"] as const;
export type SimulateScenarioId = (typeof SIMULATE_SCENARIOS)[number];

const runBody = z.object({
  scenario: z.enum(SIMULATE_SCENARIOS),
  /** Commitment kind scope (inflow optional; remove-kind required). */
  kind: z.string().optional(),
  /** Inflow change, percent (inflow scenario). */
  deltaPct: z.coerce.number().min(-100).max(500).optional(),
  /** Headcount added (hire scenario). */
  hires: z.coerce.number().int().min(0).max(100).optional(),
});

export type SimulateRunBody = z.infer<typeof runBody>;

export const intelSimulate = {
  options: {
    method: "GET",
    path: () => "/api/intel/simulate",
    response: optionsResponse,
  } satisfies Operation<void, void, void, SimulateOptionsResponse>,
  run: {
    method: "POST",
    path: () => "/api/intel/simulate",
    body: runBody,
    response: scenarioResult,
  } satisfies Operation<void, void, SimulateRunBody, SimulateScenarioResult>,
};
